import { useEffect, useRef, useState } from 'react';
import { Alert, Button, Form, Input, Skeleton } from 'antd';
import { Link } from 'react-router-dom';

import { api } from '../api/client';
import { API_RESET_PASSWORD_URL } from '../utils';
import { takeLinkToken } from '../utils/linkToken';

// Matches the API's Joi rule for newPassword (and change-password's).
const MIN_PASSWORD_LENGTH = 8;

/**
 * Landing page for the `${APP_BASE_URL}/reset-password?token=…` link. Public,
 * and deliberately NOT a login: the API answers 204 with no body and no
 * session token, so a leaked link can never hand out a session. The user
 * finishes at the normal login form.
 */
export default function ResetPassword() {
	// null = the link has not been read yet, '' = it carried no token.
	const [token, setToken] = useState(null);
	const [status, setStatus] = useState('form');
	const [submitting, setSubmitting] = useState(false);
	const [sendError, setSendError] = useState(null);

	// Reading the token strips it from the URL, so a second read would come
	// back empty. Under StrictMode's double-mount that would turn a perfectly
	// good link into the "invalid" state — hence the guard.
	const read = useRef(false);

	useEffect(() => {
		if (read.current) return;
		read.current = true;
		setToken(takeLinkToken());
	}, []);

	const handleFinish = ({ newPassword }) => {
		setSubmitting(true);
		setSendError(null);

		api.post(API_RESET_PASSWORD_URL, { token, newPassword })
			.then(() => setStatus('success'))
			.catch(error => {
				// The flat 400 is the only answer that means "this link is
				// dead"; anything else (network, 5xx) is worth retrying with
				// the same link, so the form stays up.
				if (error?.response?.status === 400) {
					setStatus('invalid');
				} else {
					setSendError('Something went wrong. Please try again.');
				}
			})
			.finally(() => setSubmitting(false));
	};

	if (token === null) {
		return (
			<>
				<h1>Reset password</h1>
				<Skeleton active />
			</>
		);
	}

	if (status === 'success') {
		return (
			<>
				<h1>Reset password</h1>
				<Alert
					type="success"
					showIcon
					message="Password updated"
					description="Your password has been changed. Sign in with your new password."
				/>
				<p style={{ marginTop: 16 }}>
					<Link to="/login">
						<Button type="primary">Go to login</Button>
					</Link>
				</p>
			</>
		);
	}

	if (status === 'invalid' || token === '') {
		return (
			<>
				<h1>Reset password</h1>
				<Alert
					type="error"
					showIcon
					message="This link is invalid or has expired"
					description="Reset links are good for one hour and can only be used once. Request a new one to continue."
				/>
				<p style={{ marginTop: 16 }}>
					<Link to="/forgot-password">
						<Button type="primary">Request a new link</Button>
					</Link>
				</p>
			</>
		);
	}

	return (
		<>
			<h1>Reset password</h1>

			<Form className="m-1" layout="vertical" onFinish={handleFinish}>
				<Form.Item
					name="newPassword"
					label="New password"
					rules={[
						{ required: true, message: 'Enter a new password.' },
						{
							min: MIN_PASSWORD_LENGTH,
							message: `Password must be at least ${MIN_PASSWORD_LENGTH} characters.`,
						},
					]}
				>
					<Input.Password autoComplete="new-password" />
				</Form.Item>

				<Form.Item
					name="confirmPassword"
					label="Confirm password"
					dependencies={['newPassword']}
					rules={[
						{ required: true, message: 'Confirm your new password.' },
						({ getFieldValue }) => ({
							validator(_, value) {
								if (!value || getFieldValue('newPassword') === value) {
									return Promise.resolve();
								}
								return Promise.reject(new Error('The two passwords do not match.'));
							},
						}),
					]}
				>
					<Input.Password autoComplete="new-password" />
				</Form.Item>

				{sendError ? (
					<Alert type="error" showIcon message={sendError} style={{ marginBottom: 16 }} />
				) : null}

				<Button type="primary" htmlType="submit" loading={submitting}>
					Set new password
				</Button>
			</Form>
		</>
	);
}
